import { api } from '@/services/api/apiClient';
import type {
  DownloadUrlResponse,
  Industry,
  IndustryQuestions,
  ProfileResponse,
  RateLimitResponse,
  ReportsListResponse,
  ScanMode,
  SiteSummary,
  StartAnalysisResponse,
  StoreReportResponse,
  UserContext,
} from '../types/discovery.types';

/** Thrown when a Graph call needs admin consent that has not been granted. */
export class SitesConsentRequiredError extends Error {
  constructor() {
    super('Admin consent required for SharePoint access');
    this.name = 'SitesConsentRequiredError';
  }
}

function isConsentRequired(err: unknown): boolean {
  const e = err as { response?: { status?: number; data?: { error?: string } } };
  return e?.response?.status === 403 && e?.response?.data?.error === 'consent_required';
}

/**
 * All API calls require a validated API access token (audience = our client ID).
 * The backend extracts tenant_id, user_id, and email from the validated JWT
 * claims — no identity parameters are sent from the client.
 */

function authHeaders(apiToken: string) {
  return { headers: { Authorization: `Bearer ${apiToken}` } };
}

/**
 * Start a new Discovery analysis.
 *
 * Defaults to Mode A (``form_only``) — no environment scan. Pass
 * ``scanMode='sharepoint'`` with the user-selected ``selectedSiteIds`` to run
 * the opt-in crawl (Mode B).
 */
export async function startAnalysis(
  industry: Industry,
  userContext: UserContext,
  apiToken: string,
  storeReport = false,
  scanMode: ScanMode = 'form_only',
  selectedSiteIds: string[] = []
): Promise<StartAnalysisResponse> {
  const response = await api.post<StartAnalysisResponse>(
    '/readiness/analyses',
    {
      industry,
      store_report: storeReport,
      scan_mode: scanMode,
      selected_site_ids: selectedSiteIds,
      ...userContext,
    },
    // Generation is synchronous (~30-60s for the 6 sequential Claude calls), so
    // override the client's 30s default — the report is returned in this response.
    // 210s leaves headroom for a slow run while staying under the ~230s Azure
    // Functions HTTP gateway limit, so the client surfaces a clean error first.
    { ...authHeaders(apiToken), timeout: 210000 }
  );
  return response.data;
}

/**
 * Fetch pre-canned questions for an industry (common scenarios + governance).
 */
export async function getIndustryQuestions(
  industry: Industry,
  apiToken: string,
  role?: string
): Promise<IndustryQuestions> {
  const query = role ? `?role=${encodeURIComponent(role)}` : '';
  const response = await api.get<IndustryQuestions>(
    `/readiness/industries/${industry}/questions${query}`,
    authHeaders(apiToken)
  );
  return response.data;
}

/**
 * List the caller's recently used / followed SharePoint sites (Mode B opt-in).
 * Throws {@link SitesConsentRequiredError} if admin consent is not granted.
 */
export async function getRecentSites(apiToken: string): Promise<SiteSummary[]> {
  try {
    const response = await api.get<{ sites: SiteSummary[] }>(
      '/readiness/sites/recent',
      authHeaders(apiToken)
    );
    return response.data.sites ?? [];
  } catch (err) {
    if (isConsentRequired(err)) throw new SitesConsentRequiredError();
    throw err;
  }
}

/**
 * Search SharePoint sites the caller can access (Mode B opt-in).
 * Throws {@link SitesConsentRequiredError} if admin consent is not granted.
 */
export async function searchSites(
  query: string,
  apiToken: string
): Promise<SiteSummary[]> {
  try {
    const response = await api.get<{ sites: SiteSummary[] }>(
      `/readiness/sites/search?q=${encodeURIComponent(query)}`,
      authHeaders(apiToken)
    );
    return response.data.sites ?? [];
  } catch (err) {
    if (isConsentRequired(err)) throw new SitesConsentRequiredError();
    throw err;
  }
}

/**
 * Opt-in store a generated report to blob storage.
 */
export async function storeReport(
  analysisId: string,
  apiToken: string
): Promise<StoreReportResponse> {
  const response = await api.post<StoreReportResponse>(
    `/readiness/analyses/${analysisId}/store`,
    {},
    authHeaders(apiToken)
  );
  return response.data;
}

/**
 * List all stored reports for the caller's tenant (identity from token).
 */
export async function listReports(
  apiToken: string
): Promise<ReportsListResponse> {
  const response = await api.get<ReportsListResponse>(
    '/readiness/reports',
    authHeaders(apiToken)
  );
  return response.data;
}

/**
 * Get a SAS download URL for a stored report (tenant scoped by token).
 */
export async function getStoredReportUrl(
  analysisId: string,
  apiToken: string
): Promise<DownloadUrlResponse> {
  const response = await api.get<DownloadUrlResponse>(
    `/readiness/reports/${analysisId}`,
    authHeaders(apiToken)
  );
  return response.data;
}

/**
 * Delete a stored report from blob storage (tenant scoped by token).
 */
export async function deleteReport(
  analysisId: string,
  apiToken: string
): Promise<void> {
  await api.delete(`/readiness/reports/${analysisId}`, authHeaders(apiToken));
}

/**
 * Get saved user profile for form pre-population (identity from token).
 */
export async function getProfile(
  apiToken: string
): Promise<ProfileResponse | null> {
  try {
    const response = await api.get<ProfileResponse>(
      '/readiness/profile',
      authHeaders(apiToken)
    );
    return response.data;
  } catch {
    // 404 = no saved profile, which is fine
    return null;
  }
}

/**
 * Save user profile data for future form pre-population (identity from token).
 */
export async function saveProfile(
  profileData: Record<string, string>,
  apiToken: string
): Promise<void> {
  await api.put('/readiness/profile', profileData, authHeaders(apiToken));
}

/**
 * Check the caller's rate limit status (identity from token).
 */
export async function checkRateLimit(
  apiToken: string
): Promise<RateLimitResponse> {
  const response = await api.get<RateLimitResponse>(
    '/readiness/rate-limit',
    authHeaders(apiToken)
  );
  return response.data;
}
